import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null; info: string | null }

// Catches render-path exceptions so a single bad component doesn't white-screen
// the whole app. Shows the error + a "Reload" button. Hooks console for the
// stack so dev tools still see it.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ErrorBoundary] caught render error:', error);
    if (info?.componentStack) console.error(info.componentStack);
    this.setState({ info: info?.componentStack ?? null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-lg bg-slate-900 border border-rose-500/30 rounded-2xl p-6 space-y-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold text-rose-300">Something broke.</h1>
            <p className="text-sm text-slate-400">A render error crashed the page. Reloading usually fixes it; if not, the error below has the details.</p>
          </div>
          <pre className="text-xs bg-slate-950 border border-slate-800 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap text-rose-200">
            {this.state.error.message}
            {this.state.info ? `\n\nComponent stack:${this.state.info}` : ''}
          </pre>
          <div className="flex gap-2">
            <button onClick={() => window.location.reload()} className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium">Reload page</button>
            <button onClick={() => this.setState({ error: null, info: null })} className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm">Try to recover</button>
          </div>
        </div>
      </div>
    );
  }
}
